import React, { useEffect, useRef } from 'react';
import { Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { OutlookPromoTrigger } from '../services/api/outlookPromoService';
import { useOutlookPromo } from '../hooks/useOutlookPromo';
import { trackUiButtonEventAsync } from '../services/api/uiButtonStatsService';
import { getBrandingColor } from '../utils/brandingStyles';

interface OutlookPromoModalProps {
    trigger: OutlookPromoTrigger;
    /** Si true, force l'évaluation shouldShow au mount / refresh */
    enabled?: boolean;
}

/**
 * Modal Bootstrap réutilisable pour T2 (post-carto) et T3 (post-interview).
 */
const OutlookPromoModal: React.FC<OutlookPromoModalProps> = ({ trigger, enabled = true }) => {
    const { t } = useTranslation();
    const modalRef = useRef<HTMLDivElement>(null);
    const shownRef = useRef(false);
    const { refresh, ack, shouldShow, installUrl, loading } = useOutlookPromo({ autoFetch: true });
    const accent = getBrandingColor();

    useEffect(() => {
        if (!enabled || loading) return;
        let cancelled = false;
        (async () => {
            await refresh();
            if (cancelled || shownRef.current) return;
            if (!shouldShow(trigger)) return;
            shownRef.current = true;
            await ack(trigger, 'shown');
            const el = modalRef.current;
            if (!el || !(window as any).bootstrap?.Modal) return;
            const modal = (window as any).bootstrap.Modal.getOrCreateInstance(el);
            modal.show();
        })();
        return () => {
            cancelled = true;
        };
    }, [enabled, loading, trigger, refresh, shouldShow, ack]);

    const dismiss = async (action: 'dismiss' | 'cta') => {
        await ack(trigger, action);
        const el = modalRef.current;
        if (el) {
            const modal = (window as any).bootstrap?.Modal?.getInstance(el);
            modal?.hide();
        }
    };

    const handleCta = async () => {
        await trackUiButtonEventAsync(`outlook_promo.${trigger}.cta`);
        window.open(installUrl, '_blank', 'noopener,noreferrer');
        await dismiss('cta');
    };

    const handleDismiss = async () => {
        await trackUiButtonEventAsync(`outlook_promo.${trigger}.dismiss`);
        await dismiss('dismiss');
    };

    const title =
        trigger === 'first_interview'
            ? t('outlookPromo.interviewTitle', 'Installez Famla pour Outlook')
            : t('outlookPromo.mappingTitle', 'Votre cartographie est prête — et Outlook aussi');
    const description =
        trigger === 'first_interview'
            ? t(
                  'outlookPromo.interviewDescription',
                  "Merci pour votre contribution. L'extension Outlook Famla vous permet de continuer à collaborer depuis votre messagerie.",
              )
            : t(
                  'outlookPromo.mappingDescription',
                  "Installez l'extension Famla pour retrouver vos processus et signaler des points de friction directement dans Outlook.",
              );

    return (
        <div
            className="modal fade"
            id={`outlookPromoModal_${trigger}`}
            tabIndex={-1}
            aria-hidden="true"
            ref={modalRef}
        >
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content" style={{ borderRadius: 16, overflow: 'hidden' }}>
                    <div className="modal-body p-4">
                        <div className="d-flex align-items-start gap-3 mb-3">
                            <span
                                className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                                style={{ width: 48, height: 48, background: `${accent}22`, color: accent }}
                            >
                                <Mail size={22} />
                            </span>
                            <div>
                                <h5 className="mb-2">{title}</h5>
                                <p className="mb-0 text-muted" style={{ fontSize: '0.95rem', lineHeight: 1.55 }}>
                                    {description}
                                </p>
                            </div>
                        </div>
                        <div className="d-flex flex-column gap-2 mt-4">
                            <button type="button" className="btn btn-theme w-100" onClick={handleCta}>
                                {t('outlookPromo.installCta', "Installer l'extension Outlook")}
                            </button>
                            <button type="button" className="btn btn-link text-muted" onClick={handleDismiss}>
                                {t('outlookPromo.later', 'Plus tard')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OutlookPromoModal;
